import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useBookingStore from "../../legacy/store/bookingStore";
import { getPropertyQuotes } from "../../api/elivaas";
import { getCheckoutStayData, previewBookingPrice } from "../../legacy/api/propertyDetail";
import { apiGet } from "../../legacy/services/apiClient";
import { normalizeBookingPricing } from "../../utils/bookingPricing";
import DateRangePicker from "../../legacy/components/common/DateRangePicker";
import MobileBookingProgress from "../components/MobileBookingProgress";
import "../styles/mobile-theme.css";

const pad2 = (value) => String(value).padStart(2, "0");
const iso = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};
const addDays = (value, days = 1) => {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + days);
  return iso(date);
};
const tomorrow = (value) => addDays(value, 1);
const formatDisplayDate = (value) => {
  if (!value) return "-";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
const parseIsoDate = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeProviderInventoryDays = (payload = {}) => {
  const source =
    (Array.isArray(payload?.inventories) && payload.inventories) ||
    (Array.isArray(payload?.data?.inventories) && payload.data.inventories) ||
    (Array.isArray(payload?.data) && payload.data) ||
    [];
  return source
    .map((item) => {
      const date = item?.date || item?.inventoryDate || "";
      if (!date) return null;
      const blocked = item?.stopSell === true || Number(item?.quantity || 0) <= 0;
      return {
        date,
        status: blocked ? "blocked" : "open",
        available: !blocked,
        min_nights: Number(item?.minimumStay || 0) || null,
        max_nights: Number(item?.maximumStay || 0) || null,
      };
    })
    .filter(Boolean);
};

const readDiscountLines = (source = {}) => {
  source = source || {};
  const pricing = source.pricing || {};
  const rawLines =
    pricing.discountLines ||
    pricing.discount_lines ||
    pricing.discounts ||
    source.discountLines ||
    source.discount_lines ||
    source.discounts ||
    [];
  const lines = Array.isArray(rawLines)
    ? rawLines
        .map((item) => ({
          label: item.label || item.name || item.title || item.code || "Discount",
          amount: Math.abs(Number(item.amount || item.value || item.discount || 0)),
        }))
        .filter((item) => item.amount > 0)
    : [];
  const coupon = source.appliedCoupon || {};
  const fallback = Math.abs(Number(
    pricing.discount ||
      source.discount ||
      source.discount_amount ||
      coupon.amount ||
      coupon.discount ||
      coupon.discount_amount ||
      coupon.value ||
      0
  ));
  if (!lines.length && fallback > 0) return [{ label: coupon.code || coupon.label || "Discount", amount: fallback }];
  return lines;
};

const readOfferLines = (source = {}) => {
  source = source || {};
  const rawOffers =
    source?.pricing?.eligible_card_offers ||
    source?.eligible_card_offers ||
    source?.cardOffers ||
    source?.offers ||
    [];
  return Array.isArray(rawOffers)
    ? rawOffers
        .map((item) => ({
          label: item.label || item.name || item.title || item.code || "Offer",
          description: item.description || item.short_description || item.subtitle || "",
        }))
        .filter((item) => item.label)
        .slice(0, 3)
    : [];
};

export default function MobileDateGuestSelector() {
  const navigate = useNavigate();
  const storedIn = useBookingStore((state) => state.checkIn);
  const storedOut = useBookingStore((state) => state.checkOut);
  const storedGuests = useBookingStore((state) => state.guests);
  const setDates = useBookingStore((state) => state.setDates);
  const setGuests = useBookingStore((state) => state.setGuests);
  const stayId = useBookingStore((state) => state.stayId);
  const stayData = useBookingStore((state) => state.stayData);
  const appliedCoupon = useBookingStore((state) => state.appliedCoupon);
  const pricingBreakdown = useBookingStore((state) => state.pricingBreakdown);
  const setSelection = useBookingStore((state) => state.setSelection);
  const setStayData = useBookingStore((state) => state.setStayData);
  const setPricingBreakdown = useBookingStore((state) => state.setPricingBreakdown);
  const storedQuoteId = useBookingStore((state) => state.quoteId);
  const storedRatePlanCode = useBookingStore((state) => state.ratePlanCode);
  const today = useMemo(() => iso(new Date()), []);
  const earliestCheckIn = useMemo(() => tomorrow(tomorrow(today)), [today]);
  const initialCheckIn = storedIn && storedIn >= earliestCheckIn ? storedIn : earliestCheckIn;
  const [checkIn, setCheckIn] = useState(initialCheckIn);
  const [checkOut, setCheckOut] = useState(storedOut && storedOut > initialCheckIn ? storedOut : tomorrow(initialCheckIn));
  const [adults, setAdults] = useState(Math.max(1, Number(storedGuests) || 2));
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [calendarDays, setCalendarDays] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState("");
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const seededDate = parseIsoDate(initialCheckIn) || new Date();
    return new Date(seededDate.getFullYear(), seededDate.getMonth(), 1);
  });
  const minimumNights = Math.max(1, Number(stayData?.min_nights || 1));
  const minimumCheckOut = useMemo(() => addDays(checkIn || earliestCheckIn, minimumNights), [checkIn, earliestCheckIn, minimumNights]);
  const maximumGuests = Math.max(1, Number(stayData?.max_guests || stayData?.guests || 99));
  const blockedCalendarDates = useMemo(
    () =>
      new Set(
        (Array.isArray(calendarDays) ? calendarDays : [])
          .filter((day) => day?.date && (day.available === false || day.status === "blocked" || day.status === "booked"))
          .map((day) => day.date)
      ),
    [calendarDays]
  );
  const rangeHasBlockedDates = useCallback(
    (startIso, endIso) => {
      if (!startIso || !endIso || !blockedCalendarDates.size) return false;
      for (const day of blockedCalendarDates) {
        if (day >= startIso && day < endIso) return true;
      }
      return false;
    },
    [blockedCalendarDates]
  );
  const nights = useMemo(() => {
    if (!checkIn || !checkOut || checkOut <= checkIn) return 0;
    return Math.ceil((new Date(`${checkOut}T12:00:00`) - new Date(`${checkIn}T12:00:00`)) / 86400000);
  }, [checkIn, checkOut]);
  const currency = stayData?.currency || "INR";
  const pricing = useMemo(() => {
    if (pricingBreakdown) {
      return normalizeBookingPricing(pricingBreakdown, { nights, currency });
    }
    const fallbackNightlyRate = Number(
      stayData?.price ||
        stayData?.nightly_rate ||
        stayData?.base_price ||
        stayData?.display_sell_price ||
        stayData?.final_per_night_display_price ||
        stayData?.sell_price_per_night ||
        stayData?.sellPricePerNight ||
        stayData?.price_per_night ||
        stayData?.base_price_per_night ||
        stayData?.price_from ||
        0
    );
    const fallbackBase = fallbackNightlyRate * Math.max(0, nights);
    const fallbackGst = Math.round(fallbackBase * 0.18);
    return {
      basePrice: fallbackBase,
      taxableSubtotal: fallbackBase,
      gstAmount: fallbackGst,
      finalPayable: fallbackBase ? fallbackBase + fallbackGst : 0,
      total: fallbackBase ? fallbackBase + fallbackGst : 0,
      nightlyRate: fallbackNightlyRate,
      currency,
      discountLines: [],
      breakdownItems: [],
    };
  }, [currency, nights, pricingBreakdown, stayData]);
  const nightlyRate = Number(pricing?.nightlyRate || 0);
  const totalBeforeTax = Number(pricing?.taxableSubtotal ?? pricing?.basePrice ?? 0);
  const gst = Number(pricing?.gstAmount ?? pricing?.tax ?? 0);
  const finalPayable = Number(pricing?.finalPayable ?? pricing?.total ?? 0);
  const formatMoney = (amount) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(amount || 0));
  const services = useMemo(() => {
    const raw = stayData?.addons || stayData?.addOns || stayData?.services || [];
    return Array.isArray(raw) ? raw.slice(0, 4) : [];
  }, [stayData]);
  const pricingSource = useMemo(
    () => ({
      ...(stayData || {}),
      pricing: {
        ...(stayData?.pricing || {}),
        ...(pricingBreakdown || {}),
      },
      appliedCoupon,
    }),
    [appliedCoupon, pricingBreakdown, stayData]
  );
  const discountLines = useMemo(() => readDiscountLines(pricingSource), [pricingSource]);
  const offerLines = useMemo(() => {
    const applied = pricingSource.appliedCoupon
      ? [{
          label: pricingSource.appliedCoupon.code || pricingSource.appliedCoupon.label || pricingSource.appliedCoupon.name || "Applied offer",
          description: pricingSource.appliedCoupon.description || "Discount applied to this booking.",
        }]
      : [];
    return [...applied, ...readOfferLines(pricingSource)].slice(0, 3);
  }, [pricingSource]);

  useEffect(() => {
    const propertyId = stayData?.id || stayId;
    if (!propertyId || !checkIn || !checkOut || checkOut <= checkIn || adults + children < 1) {
      setQuoteLoading(false);
      setQuoteError("");
      return;
    }

    let active = true;
    const timerId = window.setTimeout(() => {
      setQuoteLoading(true);
      setQuoteError("");
      previewBookingPrice({
        stayId: propertyId,
        checkIn,
        checkOut,
        guests: adults + children,
        adults,
        children,
        quoteId: storedQuoteId || undefined,
        ratePlanCode: storedRatePlanCode || undefined,
      })
        .then((payload) => {
          if (!active) return;
          setPricingBreakdown({
            ...payload,
            nights,
            currency: payload?.currency || currency,
          });
        })
        .catch((err) => {
          if (!active) return;
          setQuoteError(err?.message || "Pricing is unavailable for the selected dates.");
        })
        .finally(() => {
          if (active) setQuoteLoading(false);
        });
    }, 120);

    return () => {
      active = false;
      window.clearTimeout(timerId);
    };
  }, [
    adults,
    checkIn,
    checkOut,
    children,
    currency,
    nights,
    setPricingBreakdown,
    stayData?.id,
    stayId,
    storedQuoteId,
    storedRatePlanCode,
  ]);

  useEffect(() => {
    if (stayData || !stayId) return;
    let active = true;
    getCheckoutStayData(stayId)
      .then((data) => {
        if (!active || !data) return;
        const fetchedPrice = Number(
          data.price ||
            data.nightly_rate ||
            data.base_price ||
            data.display_sell_price ||
            data.final_per_night_display_price ||
            data.sell_price_per_night ||
            data.sellPricePerNight ||
            data.price_per_night ||
            data.base_price_per_night ||
            data.price_from ||
            0
        );
        const existingPrice = Number(stayData?.price || stayData?.nightly_rate || stayData?.base_price || 0);
        const price = fetchedPrice || existingPrice;
        setStayData({
          ...stayData,
          ...data,
          price,
          nightly_rate: data.nightly_rate || price,
          base_price: data.base_price || price,
        });
      })
      .catch(() => {});
    return () => { active = false; };
  }, [setStayData, stayData, stayId]);

  useEffect(() => {
    const propertyKey = stayData?.slug || stayData?.id || stayId;
    const providerPropertyId =
      stayData?.provider_property_id ||
      (String(stayId || "").startsWith("provider:elivaas:")
        ? String(stayId).replace("provider:elivaas:", "")
        : "");
    if (!propertyKey && !providerPropertyId) return;

    let active = true;
    const from = iso(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1));
    const to = iso(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 2, 0));

    const loadCalendar = async () => {
      setCalendarLoading(true);
      setCalendarError("");
      if ((stayData?.provider_code === "elivaas" || providerPropertyId) && providerPropertyId) {
        try {
          const payload = await apiGet(
            `/api/providers/elivaas/properties/${encodeURIComponent(providerPropertyId)}/inventories?fromDate=${from}&toDate=${to}`
          );
          if (!active) return;
          setCalendarDays(normalizeProviderInventoryDays(payload));
          setCalendarLoading(false);
          return;
        } catch (err) {
          if (!active) return;
          setCalendarDays([]);
          setCalendarError(err?.message || "Failed to load available dates.");
          setCalendarLoading(false);
          return;
        }
      }

      try {
        const payload = await apiGet(
          `/api/public/properties/${encodeURIComponent(propertyKey)}/availability?from=${from}&to=${to}`
        );
        const days = Array.isArray(payload?.days) ? payload.days : Array.isArray(payload?.data?.days) ? payload.data.days : [];
        if (!active) return;
        setCalendarDays(days);
        setCalendarLoading(false);
      } catch (err) {
        if (!active) return;
        setCalendarDays([]);
        setCalendarError(err?.message || "Failed to load available dates.");
        setCalendarLoading(false);
      }
    };

    loadCalendar();
    return () => {
      active = false;
    };
  }, [calendarMonth, stayData?.id, stayData?.provider_code, stayData?.provider_property_id, stayData?.slug, stayId]);

  useEffect(() => {
    if (checkIn && checkOut && checkOut < minimumCheckOut) setCheckOut(minimumCheckOut);
  }, [checkIn, checkOut, minimumCheckOut]);

  useEffect(() => {
    if (!checkIn) return;
    const seededDate = parseIsoDate(checkIn);
    if (!seededDate) return;
    const nextMonth = new Date(seededDate.getFullYear(), seededDate.getMonth(), 1);
    if (
      calendarMonth.getFullYear() !== nextMonth.getFullYear() ||
      calendarMonth.getMonth() !== nextMonth.getMonth()
    ) {
      setCalendarMonth(nextMonth);
    }
  }, [calendarMonth, checkIn]);

  useEffect(() => {
    let nextCheckIn = checkIn;
    let nextCheckOut = checkOut;

    if (nextCheckIn && blockedCalendarDates.has(nextCheckIn)) {
      nextCheckIn = "";
      nextCheckOut = "";
    }
    if (nextCheckIn && nextCheckOut && rangeHasBlockedDates(nextCheckIn, nextCheckOut)) {
      nextCheckOut = "";
    }

    if (nextCheckIn !== checkIn) setCheckIn(nextCheckIn);
    if (nextCheckOut !== checkOut) setCheckOut(nextCheckOut);
  }, [blockedCalendarDates, checkIn, checkOut, rangeHasBlockedDates]);

  const updateDateRange = (nextCheckIn, nextCheckOut) => {
    setError("");
    setCheckIn(nextCheckIn || "");
    setCheckOut(nextCheckOut || "");
  };

  const done = async () => {
    if (!checkIn || !checkOut || checkOut <= checkIn) {
      setError("Check-out must be after check-in.");
      return;
    }
    if (nights < minimumNights) {
      setError(`This stay requires at least ${minimumNights} night${minimumNights > 1 ? "s" : ""}.`);
      return;
    }
    if (blockedCalendarDates.has(checkIn) || rangeHasBlockedDates(checkIn, checkOut)) {
      setError("Selected dates include booked dates. Please choose different dates.");
      return;
    }
    if (adults + children > maximumGuests) {
      setError(`Maximum capacity is ${maximumGuests} guests.`);
      return;
    }
    setChecking(true);
    setError("");
    const providerId = String(stayId || "").startsWith("provider:elivaas:")
      ? String(stayId).replace("provider:elivaas:", "")
      : stayData?.provider_code === "elivaas" ? stayData?.provider_property_id : "";
    if (providerId) {
      try {
        const response = await getPropertyQuotes(providerId, { checkIn, checkOut, adults, children, quantity: 1 });
        const quotes = Array.isArray(response) ? response : response?.quotes || response?.list || [];
        const quote = quotes[0];
        if (!quote) {
          setError("This property is unavailable for the selected dates. Please choose different dates.");
          setChecking(false);
          return;
        }
        setSelection({
          quoteId: quote.quoteId || quote.quote_id || quote.id || "",
          ratePlanCode: quote.ratePlanCode || quote.rate_plan_code || "",
        });
      } catch (err) {
        setError(err?.message || "Availability could not be checked. Please try again.");
        setChecking(false);
        return;
      }
    }
    setDates(checkIn, checkOut);
    setGuests(adults + children);
    if (pricingBreakdown) {
      setPricingBreakdown({
        ...pricingBreakdown,
        nights,
        currency,
      });
    }
    setChecking(false);
    navigate("/booking/guest");
  };

  const Counter = ({ label, hint, value, setValue, minimum = 0 }) => (
    <div className="hv-guest-row">
      <div><strong>{label}</strong><small>{hint}</small></div>
      <div className="hv-counter">
        <button type="button" onClick={() => setValue(Math.max(minimum, value - 1))} aria-label={`Remove ${label}`}>-</button>
        <span>{value}</span>
        <button type="button" onClick={() => setValue(value + 1)} aria-label={`Add ${label}`}>+</button>
      </div>
    </div>
  );

  return (
    <div className="hv-mobile-shell hv-flow-page hv-ref-booking-screen">
      <MobileBookingProgress active={1} crumb="Home › Guests" />
      <main className="hv-ref-booking-main">
        <section className="hv-ref-booking-card hv-ref-guest-setup">
          <div className="hv-ref-booking-row">
            <span>Guest details & customisations</span>
            <button type="button" onClick={() => navigate(-1)}>‹ Back</button>
          </div>
          <h1>Set up your stay</h1>
          <div className="hv-ref-date-panel">
            <div>
              <strong>{formatDisplayDate(checkIn)} → {formatDisplayDate(checkOut)}</strong>
              <p>{nights || minimumNights} night{(nights || minimumNights) === 1 ? "" : "s"}</p>
            </div>
            <button type="button" onClick={() => setDatePickerOpen((value) => !value)}>
              {datePickerOpen ? "Done" : "Change dates"}
            </button>
          </div>
          {datePickerOpen ? (
            <div className="hv-ref-mobile-calendar" aria-label="Date selection">
              <DateRangePicker
                checkIn={checkIn}
                checkOut={checkOut}
                onChange={updateDateRange}
                minDate={earliestCheckIn}
                minNights={minimumNights}
                days={calendarDays}
                loading={calendarLoading}
                error={calendarError}
                month={calendarMonth}
                onMonthChange={setCalendarMonth}
                mode="inline"
                showInputs={false}
                closeOnComplete={false}
              />
            </div>
          ) : null}
          <div className="hv-ref-guest-grid">
            <div className="hv-ref-guest-counter">
              <span>Total guests</span>
              <button type="button" onClick={() => setAdults(Math.max(1, adults - 1))}>−</button>
              <strong>{adults + children}</strong>
              <button type="button" onClick={() => (adults + children < maximumGuests ? setAdults(adults + 1) : null)}>+</button>
            </div>
            <div className="hv-ref-arrival"><span>Arrival window</span><strong>2 PM – 4 PM</strong></div>
          </div>
          <details className="hv-ref-extra-guests">
            <summary>Advanced guest split</summary>
            <Counter label="Adults" hint="12+ years" value={adults} setValue={setAdults} minimum={1} />
            <Counter label="Children" hint="5-11 years" value={children} setValue={setChildren} />
            <Counter label="Infants" hint="0-4 years" value={infants} setValue={setInfants} />
            <p className="hv-muted-row">Maximum {maximumGuests} guests</p>
          </details>
          <div className="hv-ref-rate-strip">
            {quoteLoading ? "Refreshing price..." : `${nights || minimumNights} nights x ${formatMoney(nightlyRate)} / night`}
          </div>
        </section>

        {services.length ? (
          <section className="hv-ref-booking-card">
            <div className="hv-ref-eyebrow">Your selected services</div>
            <h2>Carried from the property page</h2>
            <p className="hv-ref-sub">Tap to add or remove — the folio updates instantly.</p>
            <div className="hv-ref-addons">
              {services.map((service, index) => (
                <div key={service.id || service.name || index}>
                  <span aria-hidden="true">✦</span>
                  <section>
                    <strong>{service.name || service.title}</strong>
                    <small>{service.description || service.short_description || ""}</small>
                    <b>{formatMoney(service.amount ?? service.price)} <i>/ {service.unit || "stay"}</i></b>
                  </section>
                  <button type="button">+ Add</button>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="hv-ref-booking-card hv-ref-price-card">
          <strong>{nightlyRate ? formatMoney(nightlyRate) : "Rate"} <small>/night</small></strong>
          <div><span>Base Price</span><b>{formatMoney(totalBeforeTax)}</b></div>
          {discountLines.map((line, index) => (
            <div className="discount" key={`${line.label}-${index}`}><span>{line.label}</span><b>-{formatMoney(line.amount)}</b></div>
          ))}
          <div><span>GST</span><b>{formatMoney(gst)}</b></div>
          <div className="total"><span>Final Payable Amount</span><b>{formatMoney(finalPayable)}</b></div>
        </section>
        {quoteError ? <p className="hv-flow-error" role="alert">{quoteError}</p> : null}
        {offerLines.length ? (
          <section className="hv-ref-booking-card hv-ref-offer-card">
            <div className="hv-ref-eyebrow">Discounted offers</div>
            {offerLines.map((offer, index) => (
              <div key={`${offer.label}-${index}`}>
                <strong>{offer.label}</strong>
                {offer.description ? <small>{offer.description}</small> : null}
              </div>
            ))}
          </section>
        ) : null}
        {error ? <p className="hv-flow-error" role="alert">{error}</p> : null}
      </main>
      <div className="hv-ref-bar">
        <button className="hv-ref-total" type="button"><span>Grand total</span><strong>{formatMoney(finalPayable)}</strong></button>
        <button className="hv-ref-book" type="button" onClick={done} disabled={checking}>{checking ? "Checking…" : "Review →"}</button>
      </div>
    </div>
  );
}
